
import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import ParticleBackground from '@/components/ParticleBackground';
const logo = '/assets/Touchworks-b.svg';
import { useAuth } from '@/contexts/AuthContext';

// Adapt Shadcn UI "Field" components using standard implementations
const Field = ({ className, children, ...props }) => (
    <div className={`grid gap-2 ${className}`} {...props}>{children}</div>
);
const FieldLabel = ({ className, ...props }) => (
    <Label className={className} {...props} />
);
const FieldGroup = ({ className, children, ...props }) => (
    <div className={`grid gap-4 ${className}`} {...props}>{children}</div>
);
const FieldSeparator = ({ children, className }) => (
    <div className={`relative flex items-center py-4 ${className}`}>
        <div className="flex-grow border-t border-white/10"></div>
        <span className="flex-shrink-0 mx-4 text-muted-foreground text-xs uppercase">{children}</span>
        <div className="flex-grow border-t border-white/10"></div>
    </div>
);

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, isAuthenticated, isLoading: authLoading, user } = useAuth();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // Determine the correct redirect based on user role
    const getRedirectPath = (userData) => {
        // If user had a specific page they were trying to visit, honor it.
        // A query string junto: é ela que carrega o deep link da conversa
        // (?ticket=...&message=...) de quem clicou no link deslogado.
        if (location.state?.from?.pathname && location.state.from.pathname !== '/internal/dashboard') {
            return `${location.state.from.pathname}${location.state.from.search || ''}`;
        }
        // Regular admins go directly to their own account's implementation
        if (userData && !userData.isSuperAdmin && userData.accountId) {
            return `/internal/implementation/${userData.accountId}`;
        }
        // Super admins go to global dashboard
        return '/internal/dashboard';
    };

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated && !authLoading && user) {
            navigate(getRedirectPath(user), { replace: true });
        }
    }, [isAuthenticated, authLoading, navigate, user]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const userData = await login(username, password);
            navigate(getRedirectPath(userData), { replace: true });
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className="flex min-h-screen flex-col items-center justify-center p-6 md:p-10 bg-background text-foreground font-['Space_Grotesk'] relative overflow-hidden">

            {/* Global Background Particles - Fixed Z-0 */}
            <ParticleBackground />

            {/* Main Card Container - Split Layout */}
            <div className="relative z-20 w-full max-w-sm md:max-w-4xl">
                <Card className="overflow-hidden border border-white/5 bg-surface/95 backdrop-blur-sm shadow-2xl">
                    <CardContent className="grid p-0 md:grid-cols-2">

                        {/* LEFT SIDE: Form & Actions */}
                        <form onSubmit={handleLogin} className="p-6 md:p-8 flex flex-col justify-center">
                            <FieldGroup>
                                {/* Header */}
                                <div className="flex flex-col items-center gap-2 text-center mb-6">
                                    <h1 className="text-2xl font-bold text-foreground-strong">Bem-vindo</h1>
                                    <p className="text-muted-foreground text-sm">
                                        Entre na sua conta Touchworks
                                    </p>
                                </div>

                                {/* Inputs */}
                                <Field>
                                    <FieldLabel htmlFor="email" className="text-foreground-strong">Email</FieldLabel>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="seu@email.com"
                                        required
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        className="bg-background border-border text-foreground-strong placeholder:text-placeholder focus-visible:ring-cyan-500 h-10"
                                    />
                                </Field>
                                <Field>
                                    <div className="flex items-center">
                                        <FieldLabel htmlFor="password" className="text-foreground-strong">Senha</FieldLabel>
                                        <Link to="/forgot-password" className="ml-auto text-xs underline-offset-2 hover:underline text-cyan-500 hover:text-cyan-400">
                                            Esqueceu a senha?
                                        </Link>
                                    </div>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="bg-background border-border text-foreground-strong placeholder:text-placeholder pr-10 focus-visible:ring-cyan-500 h-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground-strong"
                                        >
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </Field>

                                {error && (
                                    <div className="text-sm text-red-500 text-center">
                                        {error}
                                    </div>
                                )}

                                <Field>
                                    <Button
                                        type="submit"
                                        disabled={isLoading}
                                        className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold h-10"
                                    >
                                        {isLoading ? 'Entrando...' : 'Entrar'}
                                    </Button>
                                </Field>

                                {/* Social Login (Visual Only) */}
                                <FieldSeparator>Ou continue com</FieldSeparator>

                                <div className="grid grid-cols-3 gap-4">
                                    <Button variant="outline" type="button" className="border-border hover:bg-surface-hover bg-background">
                                        <svg className="h-5 w-5 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" /></svg>
                                    </Button>
                                    <Button variant="outline" type="button" className="border-border hover:bg-surface-hover bg-background">
                                        <svg className="h-5 w-5 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" /></svg>
                                    </Button>
                                    <Button variant="outline" type="button" className="border-border hover:bg-surface-hover bg-background">
                                        <svg className="h-5 w-5 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.358-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a2.264 2.264 0 0 1 1.088-.285z" /></svg>
                                    </Button>
                                </div>

                                {/* Footer Links */}
                                <div className="text-center text-xs text-muted-foreground mt-4">
                                    Não tem conta? <a href="#" className="underline hover:text-foreground-strong">Cadastre-se</a>
                                </div>
                            </FieldGroup>
                        </form>

                        {/* RIGHT SIDE: Visual / Image */}
                        <div className="relative hidden md:flex flex-col items-center justify-center p-8 bg-[#03030a] overflow-hidden border-l border-white/5">
                            {/* Background Image/Gradient */}
                            <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 to-purple-900/10 z-0"></div>

                            {/* Content Overlay */}
                            <div className="relative z-20 flex flex-col items-center gap-6 text-center max-w-[280px]">
                                <img src={logo} alt="Touchworks Logo" className="h-8 w-auto mb-4" /> {/* Adjusted size */}
                                <blockquote className="space-y-2">
                                    <p className="text-lg font-medium text-foreground-strong">
                                        &ldquo;Você não precisa só de ferramenta, precisa de método e estratégia.&rdquo;
                                    </p>
                                    <footer className="text-sm text-muted-foreground">Wenendy Silva</footer>
                                </blockquote>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Terms Footer - Below Card */}
                <div className="text-center text-xs text-muted-foreground mt-6">
                    Ao continuar, você concorda com nossos <a href="#" className="underline hover:text-foreground-strong">Termos de Serviço</a> e <a href="#" className="underline hover:text-foreground-strong">Política de Privacidade</a>.
                </div>
            </div>

            {/* Rainbow Gradient Border */}
            <div className="fixed bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 z-50"></div>
        </div>
    );
}
